import React from 'react';
import { weekdayShort, dayNum, weekdayLong } from './format';

export function MixedIcon() {
  // Camion + vehiculo: indicador no dependiente solo del color (forma + etiqueta de texto)
  return (
    <svg className="tc-sched__mixicon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M1 6a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v2h3.2c.6 0 1.2.3 1.6.8l2.2 2.8c.3.4.5.9.5 1.4V16a2 2 0 0 1-2 2h-.6a2.5 2.5 0 0 1-4.8 0H9.4a2.5 2.5 0 0 1-4.8 0H4a2 2 0 0 1-2-2zm17.3 6-1.4-2H14v2zM7 17.2a1 1 0 1 0 0-2 1 1 0 0 0 0 2m10 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2" />
    </svg>
  );
}

export function MixedBadge({ t }) {
  return (
    <span className="tc-sched__badge">
      <MixedIcon />
      <span>{t.mixed}</span>
    </span>
  );
}

function Times({ slots, t }) {
  if (!slots.length) return <span className="tc-sched__none" aria-label={t.noDeparture}>—</span>;
  return (
    <ul className="tc-sched__times">
      {slots.map((s, i) => (
        <li key={`${s.time}-${i}`} className={s.mixed ? 'tc-sched__time tc-sched__time--mixed' : 'tc-sched__time'}>
          <span className="tc-sched__hour">{s.time}</span>
          {s.mixed ? <MixedBadge t={t} /> : null}
        </li>
      ))}
    </ul>
  );
}

const cls = (...a) => a.filter(Boolean).join(' ');

/** Escritorio: origenes en filas, dias en columnas. */
/** Selector de dia: el propio encabezado de la tabla/matriz (un solo nivel visual de dias). */
function DayButton({ day, t, onSelect, variant }) {
  const L = t.locale;
  const label = `${weekdayLong(L, day.iso)} ${dayNum(day.iso)}${day.isToday ? `, ${t.today}` : ''}`;
  return (
    <button
      type="button"
      className={cls('tc-sched__btn', 'tc-sched__daybtn', variant && `tc-sched__daybtn--${variant}`, day.isSelected && 'is-selected', day.isToday && 'is-today')}
      aria-pressed={day.isSelected}
      aria-label={label}
      onClick={() => onSelect(day.index)}
    >
      <span className="tc-sched__wd" aria-hidden="true">{weekdayShort(L, day.iso)}</span>
      <span className="tc-sched__dn" aria-hidden="true">{dayNum(day.iso)}</span>
      {day.isToday ? <span className="tc-sched__todaytag" aria-hidden="true">{t.today}</span> : null}
    </button>
  );
}

/** Escritorio: origenes en filas, dias en columnas. La columna seleccionada se resalta completa. */
export function DesktopTable({ week, t, onSelect }) {
  return (
    <div className="tc-sched__tablewrap tc-sched__desktop">
      <table className="tc-sched__table">
        <caption className="tc-sched__sr">{t.tableCaption}</caption>
        <thead>
          <tr>
            <th scope="col" className="tc-sched__corner"><span className="tc-sched__sr">{t.departingFrom}</span></th>
            {week.days.map((d) => (
              <th key={d.iso} scope="col" className={cls('tc-sched__colhead', d.isSelected && 'is-selected', d.isToday && 'is-today')} aria-current={d.isToday ? 'date' : undefined}>
                <DayButton day={d} t={t} onSelect={onSelect} variant="col" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {week.origins.map((o) => (
            <tr key={o.id}>
              <th scope="row" className="tc-sched__rowhead">
                <span className="tc-sched__from">{t.departingFrom}</span>
                <span className="tc-sched__origin">{t.origin[o.id]}</span>
              </th>
              {week.days.map((d) => (
                <td key={d.iso} className={cls('tc-sched__cell', d.isSelected && 'is-selected')}>
                  <Times slots={o.slots[d.iso]} t={t} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Movil: dias en filas, origenes en columnas. La fila del dia es el selector y se resalta completa. */
export function MobileMatrix({ week, t, onSelect }) {
  return (
    <div className="tc-sched__tablewrap tc-sched__mobile">
      <table className="tc-sched__table tc-sched__table--matrix">
        <caption className="tc-sched__sr">{t.tableCaption}</caption>
        <thead>
          <tr>
            <th scope="col" className="tc-sched__corner"><span className="tc-sched__sr">{t.days}</span></th>
            {week.origins.map((o) => (
              <th key={o.id} scope="col" className="tc-sched__colhead tc-sched__colhead--origin">
                <span className="tc-sched__from">{t.departingFrom}</span>
                <span className="tc-sched__origin">{t.origin[o.id]}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {week.days.map((d) => (
            <tr key={d.iso} className={cls(d.isSelected && 'is-selected', d.isToday && 'is-today')}>
              <th scope="row" className="tc-sched__rowhead tc-sched__rowhead--day" aria-current={d.isToday ? 'date' : undefined}>
                <DayButton day={d} t={t} onSelect={onSelect} variant="row" />
              </th>
              {week.origins.map((o) => (
                <td key={o.id} className={cls('tc-sched__cell', d.isSelected && 'is-selected')}>
                  <Times slots={o.slots[d.iso]} t={t} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
